import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Drawer,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Button,
  Container,
  useTheme,
  useMediaQuery,
  Fab,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Inventory as InventoryIcon,
  Storefront as PetshopIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as FinanzasIcon,
  Pets as VetAIIcon,
  Assignment as CertificadoIcon
} from '@mui/icons-material';
import AsistenteIA from './AsistenteIA';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { patientsService } from '../services/firebaseServices';

const drawerWidth = 280;

const AdminLayout = ({ children, title, icon: TitleIcon }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [asistenteOpen, setAsistenteOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  // Carga inicial (una sola vez) de la galería pública desde los pacientes existentes
  useEffect(() => {
    try {
      if (localStorage.getItem('galeriaPublicaV1')) return;
      patientsService.rebuildPublicGallery().then((r) => {
        if (r.success) localStorage.setItem('galeriaPublicaV1', '1');
      });
    } catch (e) {
      console.error('No se pudo iniciar la carga de la galería pública:', e);
    }
  }, []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin' },
    { text: 'Gestión de Pacientes', icon: <PeopleIcon />, path: '/admin/pacientes' },
    { text: 'Certificados de Viaje', icon: <CertificadoIcon />, path: '/admin/certificados' },
    { text: 'Inventario', icon: <InventoryIcon />, path: '/admin/inventario' },
    { text: 'Petshop', icon: <PetshopIcon />, path: '/admin/petshop' },
    { text: 'Finanzas', icon: <FinanzasIcon />, path: '/admin/finanzas' },
    { text: 'Agenda Privada', icon: <CalendarIcon />, path: '/admin/agenda' }
  ];

  const drawer = (
    <Box sx={{ height: '100%', backgroundColor: '#f5f0f5' }}>
      {/* Header del drawer con logo */}
      <Box sx={{ 
        p: 2, 
        backgroundColor: '#c46494',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <Box
          component="img"
          src="/logo.png"
          alt="BelkysVet Logo"
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            objectFit: 'cover'
          }}
        />
        <Typography 
          variant="h6" 
          sx={{ 
            color: 'white',
            fontWeight: 'bold',
            fontSize: '1.1rem'
          }}
        >
          belkysveterinaria
        </Typography>
      </Box>
      
      <List sx={{ pt: 2, backgroundColor: '#f5f0f5' }}>
        {menuItems.map((item) => (
          <ListItemButton
            key={item.text}
            onClick={() => navigate(item.path)}
            sx={{
              mx: 1,
              my: 0.5,
              borderRadius: 2,
              backgroundColor: location.pathname === item.path ? '#c46494' : 'transparent',
              color: location.pathname === item.path ? 'white' : '#666',
              '&:hover': {
                backgroundColor: location.pathname === item.path ? '#b85888' : '#e8dde8',
                transform: 'translateX(4px)',
                transition: 'all 0.2s ease-in-out'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <ListItemIcon sx={{ 
              color: location.pathname === item.path ? 'white' : '#c46494',
              minWidth: 40
            }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text}
              sx={{ 
                '& .MuiTypography-root': {
                  fontWeight: location.pathname === item.path ? 600 : 400,
                  fontSize: '0.95rem'
                }
              }}
            />
          </ListItemButton>
        ))}
      </List>

      {/* Botón de Cerrar Sesión */}
      <Box sx={{ position: 'absolute', bottom: 16, left: 0, right: 0, px: 1 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            color: '#d32f2f',
            '&:hover': {
              backgroundColor: '#ffebee',
              transform: 'translateX(4px)',
              transition: 'all 0.2s ease-in-out'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          <ListItemIcon sx={{ 
            color: '#d32f2f',
            minWidth: 40
          }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Cerrar Sesión"
            sx={{ 
              '& .MuiTypography-root': {
                fontSize: '0.95rem',
                fontWeight: 500
              }
            }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: '#c46494',
          boxShadow: '0 2px 8px rgba(196, 100, 148, 0.2)'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          {TitleIcon && !isMobile && <TitleIcon sx={{ mr: 2 }} />}
          {!isMobile && (
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              {title}
            </Typography>
          )}
          {isMobile && <Box sx={{ flexGrow: 1 }} />}
          <Button 
            color="inherit" 
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{ 
              '&:hover': { 
                backgroundColor: 'rgba(255,255,255,0.1)' 
              }
            }}
          >
            Cerrar Sesión
          </Button>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          p: { xs: 1, sm: 2, md: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: '#fefefe',
          minHeight: '100vh'
        }}
      >
        <Toolbar />
        <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2 } }}>
          {children}
        </Container>

        {/* Botón flotante del asistente IA */}
        <Tooltip title="Asistente IA" placement="left">
          <Fab
            color="secondary"
            aria-label="asistente-ia"
            onClick={() => setAsistenteOpen(true)}
            sx={{
              position: 'fixed',
              bottom: { xs: 16, sm: 24 },
              right: { xs: 16, sm: 24 },
              backgroundColor: '#c46494',
              color: 'white',
              boxShadow: '0 4px 12px rgba(196, 100, 148, 0.4)',
              '&:hover': {
                backgroundColor: '#a55e88',
                boxShadow: '0 6px 16px rgba(196, 100, 148, 0.6)',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.3s ease-in-out',
              zIndex: 1000,
              width: { xs: 56, sm: 64 },
              height: { xs: 56, sm: 64 }
            }}
          >
            <VetAIIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.8rem' } }} />
          </Fab>
        </Tooltip>

        {/* Componente del asistente IA */}
        <AsistenteIA 
          open={asistenteOpen} 
          onClose={() => setAsistenteOpen(false)} 
        />
      </Box>

      {/* Indicador visual de asistente disponible */}
      {!asistenteOpen && (
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 80, sm: 94 },
            right: { xs: 16, sm: 24 },
            backgroundColor: '#602442',
            color: 'white',
            px: 2,
            py: 1,
            borderRadius: 2,
            fontSize: '0.8rem',
            fontWeight: 'bold',
            boxShadow: '0 2px 8px rgba(96, 36, 66, 0.3)',
            animation: 'pulse 2s infinite',
            zIndex: 999,
            '@keyframes pulse': {
              '0%': {
                opacity: 0.7,
                transform: 'scale(1)'
              },
              '50%': {
                opacity: 1,
                transform: 'scale(1.02)'
              },
              '100%': {
                opacity: 0.7,
                transform: 'scale(1)'
              }
            },
            display: { xs: 'none', sm: 'block' }
          }}
        >
          🤖 Asistente IA
        </Box>
      )}
    </Box>
  );
};

export default AdminLayout;
